import { User } from '@prisma/client'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { useOrganizationContext } from '~/core/context'
import { AppSessionType } from '~/features/appSession/common'
import { OnboardingValues } from '../../common'
import { StepCalendarProviders } from './components/StepCalendarProviders'
import { StepCallTransfer } from './components/StepCallTransfer'
import { StepEndCallConditions } from './components/StepEndCallConditions'
import { StepGoogleCalendarAuthorizations } from './components/StepGoogleCalendarAuthorizations'
import { StepGreeting } from './components/StepGreeting'
import { StepGuidelines } from './components/StepGuidelines'
import { getGreetingDefault, getGuidelinesDefault } from './helpers'

type Props = {
  user: User
  appSession: AppSessionType
  onSubmit: () => void
}

export const Onboarding: React.FC<Props> = ({ user, appSession, onSubmit }) => {
  const { organization } = useOrganizationContext()

  const valuesDefault: Partial<OnboardingValues> = {
    instructions: {
      greeting: getGreetingDefault(organization.company),
      guidelines: getGuidelinesDefault(organization.company),
      endCallConditions: [
        'When the caller says "goodbye" or "bye"',
        'When the caller is spam',
      ],
    },
    callTransfer: {
      activated: true,
      mode: 'single',
    },
    calendarServices: {
      activated: false,
      provider: 'outlookCalendar',
    },
  }

  return (
    <>
      <CommonClient.Onboarding.Wrapper<OnboardingValues>
        appSession={appSession}
        valuesDefault={valuesDefault}
        onSubmitted={onSubmit}
        steps={[
          {
            key: 'instructions',
            content: props => <StepGreeting {...props} />,
          },
          {
            key: 'instructions',
            content: props => <StepGuidelines {...props} />,
          },
          {
            key: 'instructions',
            content: props => <StepEndCallConditions {...props} />,
          },
          {
            key: 'callTransfer',
            content: props => <StepCallTransfer {...props} />,
          },
          {
            key: 'calendarServices',
            content: props => <StepCalendarProviders {...props} />,
          },
          {
            key: 'googleCalendarSettings',
            content: props => (
              <StepGoogleCalendarAuthorizations
                appSession={appSession}
                user={user}
                {...props}
              />
            ),
            isVisible: values =>
              values.calendarServices?.activated &&
              values.calendarServices?.provider === 'googleCalendar' &&
              !!values.calendarServices?.providerAccountId,
          },
        ]}
      />
    </>
  )
}
